import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Database } from "@/integrations/supabase/types";
import { Checkbox } from "@/components/ui/checkbox";
import { FileUploadField } from "./asset-record/FileUploadField";
import { useFileUpload } from "./asset-record/useFileUpload";
import { cn, getStatusColor } from "@/lib/utils";
import { Loader2, Scan, MapPin } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { PlacesAutocomplete } from "@/components/maps/PlacesAutocomplete";

type Asset = Database['public']['Tables']['assets']['Row'];
type AssetInsert = Database['public']['Tables']['assets']['Insert'];

interface AssetFormProps {
  initialData?: Partial<Asset> & {
    id?: number;
    name: string;
    description?: string;
    location?: string;
    latitude?: number;
    longitude?: number;
  };
  mode: "create" | "edit";
  onShowCodeScanner?: () => void;
  formRef?: React.MutableRefObject<{
    setValue: (field: keyof AssetFormValues, value: any) => void;
  } | null>;
}

// Extend the database type with required fields for the form
interface AssetFormValues extends Omit<AssetInsert, 'id' | 'created_at' | 'updated_at'> {
  name: string;
  asset_name: string;
  user_id: string | undefined;
  location?: string;
  latitude?: number | null;
  longitude?: number | null;
}

export function AssetForm({ initialData, mode, onShowCodeScanner, formRef }: AssetFormProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | undefined>();
  const [selectedImages, setSelectedImages] = useState<FileList | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<FileList | null>(null);
  const { uploadFiles } = useFileUpload();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNfcScanner, setShowNfcScanner] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const { t } = useTranslation();

  const form = useForm<AssetFormValues>({
    defaultValues: {
      name: initialData?.name || "",
      asset_name: initialData?.asset_name || "",
      asset_description: initialData?.asset_description || "",
      asset_price: initialData?.asset_price ?? null,
      category: initialData?.category || "",
      company_id: initialData?.company_id ?? null,
      place_id: initialData?.place_id ?? null,
      location: initialData?.location || "",
      latitude: initialData?.latitude ?? null,
      longitude: initialData?.longitude ?? null,
      asset_contact: initialData?.asset_contact || "",
      phone: initialData?.phone || "",
      internal_asset_id: initialData?.internal_asset_id || "",
      serial_number: initialData?.serial_number || "",
      nfc_id: initialData?.nfc_id || "",
      qr_id: initialData?.qr_id || "",
      code: initialData?.code || "",
      purchase_date: initialData?.purchase_date || "",
      warranty_expiry: initialData?.warranty_expiry || "",
      notes: initialData?.notes || "",
      private: initialData?.private || false,
      private_code: initialData?.private_code || "",
      status: initialData?.status || "Available",
      sharing_type: initialData?.sharing_type || "private",
      user_id: userId || "",
      images: initialData?.images || "",
      documents: initialData?.documents || "",
      see_info: initialData?.see_info ?? true,
      see_records: initialData?.see_records ?? true,
      add_records: initialData?.add_records ?? true,
      rating: initialData?.rating ?? null,
      conditions: initialData?.conditions ?? null,
    },
  });

  const { data: companies } = useQuery({
    queryKey: ["companies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("companies")
        .select("*")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const { data: places } = useQuery({
    queryKey: ["places", form.watch("company_id")],
    queryFn: async () => {
      const companyId = form.watch("company_id");
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("places")
        .select("*")
        .eq("company_id", companyId)
        .order("name");
      if (error) throw error;
      return data;
    },
    enabled: !!form.watch("company_id"),
  });

  useEffect(() => {
    const getUserSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUserId(session?.user?.id);
    };
    getUserSession();
  }, []);

  // Update form when userId changes
  useEffect(() => {
    if (userId) {
      form.setValue("user_id", userId);
    }
  }, [userId, form]);

  // Keep NFC ID populated when editing
  useEffect(() => {
    if (mode === "edit" && initialData?.nfc_id) {
      form.setValue("nfc_id", initialData.nfc_id);
    }
  }, [mode, initialData?.nfc_id, form]);

  // Keep location data populated when editing
  useEffect(() => {
    if (mode === "edit" && initialData) {
      if (initialData.location) {
        form.setValue("location", initialData.location, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      }
      if (typeof initialData.latitude === 'number') {
        form.setValue("latitude", initialData.latitude, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      }
      if (typeof initialData.longitude === 'number') {
        form.setValue("longitude", initialData.longitude, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    }
  }, [mode, initialData, form]);

  // Add effect to update location when company changes
  useEffect(() => {
    const selectedCompanyId = form.watch("company_id");
    const selectedPlaceId = form.watch("place_id");
    
    if (selectedCompanyId && !selectedPlaceId) {
      const selectedCompany = companies?.find(company => company.id === selectedCompanyId);
      if (selectedCompany?.location) {
        form.setValue("location", selectedCompany.location, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
        if (selectedCompany.latitude) {
          form.setValue("latitude", selectedCompany.latitude, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });
        }
        if (selectedCompany.longitude) {
          form.setValue("longitude", selectedCompany.longitude, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });
        }
      }
    }
  }, [form.watch("company_id"), form.watch("place_id"), companies]);

  // Update effect to handle place changes
  useEffect(() => {
    const selectedPlaceId = form.watch("place_id");
    if (selectedPlaceId) {
      const selectedPlace = places?.find(place => place.id === selectedPlaceId);
      if (selectedPlace?.location) {
        form.setValue("location", selectedPlace.location, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
        if (selectedPlace.latitude) {
          form.setValue("latitude", selectedPlace.latitude, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });
        }
        if (selectedPlace.longitude) {
          form.setValue("longitude", selectedPlace.longitude, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });
        }
      } else {
        // Clear location and coordinates if selected place has no location
        form.setValue("location", "", {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
        form.setValue("latitude", null, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
        form.setValue("longitude", null, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    }
  }, [form.watch("place_id"), places]);

  // Add effect to handle manual location changes
  useEffect(() => {
    const location = form.watch("location");
    const latitude = form.watch("latitude");
    const longitude = form.watch("longitude");
    
    if (!location) {
      form.setValue("latitude", null, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      form.setValue("longitude", null, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    } else if (location && !latitude && !longitude) {
      // If location is set but no coordinates, try to get coordinates
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: location }, (results, status) => {
        if (status === "OK" && results?.[0]?.geometry?.location) {
          const lat = results[0].geometry.location.lat();
          const lng = results[0].geometry.location.lng();
          
          form.setValue("latitude", lat, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });
          form.setValue("longitude", lng, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });
        }
      });
    }
  }, [form.watch("location")]);

  const onSubmit = async (data: AssetFormValues) => {
    if (isSubmitting) return; // Prevent multiple submissions
    
    try {
      setIsSubmitting(true);
      // Handle file uploads
      let imageUrls = data.images || '';
      let documentUrls = data.documents || '';

      if (selectedImages?.length) {
        const newImageUrls = await uploadFiles(selectedImages, 'asset-records');
        imageUrls = imageUrls ? `${imageUrls},${newImageUrls.join(',')}` : newImageUrls.join(',');
      }

      if (selectedDocuments?.length) {
        const newDocumentUrls = await uploadFiles(selectedDocuments, 'asset-records');
        documentUrls = documentUrls ? `${documentUrls},${newDocumentUrls.join(',')}` : newDocumentUrls.join(',');
      }

      // Transform and validate the data
      const submitData = {
        ...data,
        asset_name: data.name || data.asset_name, // Keep existing asset_name if name is empty
        nfc_id: data.nfc_id || initialData?.nfc_id, // Keep existing NFC ID if not changed
        asset_price: data.asset_price ? Number(data.asset_price) : null,
        purchase_date: data.purchase_date ? new Date(data.purchase_date).toISOString() : null,
        warranty_expiry: data.warranty_expiry ? new Date(data.warranty_expiry).toISOString() : null,
        phone: data.phone || null,
        internal_asset_id: data.internal_asset_id || null,
        serial_number: data.serial_number || null,
        notes: data.notes || null,
        private_code: data.private ? data.private_code : null,
        status: data.status || "Available",
        sharing_type: data.sharing_type || "private",
        images: imageUrls || initialData?.images || null,
        documents: documentUrls || initialData?.documents || null,
        // Ensure coordinates are cleared if location is empty
        location: data.location || null,
        latitude: data.location ? data.latitude : null,
        longitude: data.location ? data.longitude : null,
      };

      if (mode === "create") {
        const { error } = await supabase
          .from("assets")
          .insert([submitData])
          .select()
          .single();
        
        if (error) throw error;
        toast({ title: "Asset created successfully" });
      } else {
        const { error } = await supabase
          .from("assets")
          .update(submitData)
          .eq("id", initialData?.id)
          .select()
          .single();
        
        if (error) throw error;
        toast({ title: "Asset updated successfully" });
      }

      // Navigate after successful operation
      navigate("/assets");
    } catch (error: any) {
      console.error("Error submitting asset:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const startNfcScan = async () => {
    if (!('NDEFReader' in window)) {
      setScanError('NFC is not supported on this device');
      return;
    }

    try {
      setIsScanning(true);
      setScanError(null);
      const ndef = new (window as any).NDEFReader();
      await ndef.scan({
        signal: (new AbortController()).signal,
        recordType: "text",
        mediaType: "text/plain",
      });

      ndef.addEventListener("reading", ({ serialNumber }: { serialNumber: string }) => {
        form.setValue("nfc_id", serialNumber);
        setIsScanning(false);
        setShowNfcScanner(false);
        toast({
          title: "NFC Scanned",
          description: "NFC ID has been added to the form."
        });
      });

      ndef.addEventListener("error", (error: Error) => {
        setScanError(error.message);
        setIsScanning(false);
      });

    } catch (error: any) {
      setScanError(error.message || 'Failed to start NFC scanning');
      setIsScanning(false);
    }
  };

  const stopNfcScan = () => {
    setIsScanning(false);
  };

  // Expose form methods through ref
  useEffect(() => {
    if (formRef) {
      formRef.current = {
        setValue: (field: keyof AssetFormValues, value: any) => form.setValue(field, value)
      };
    }
  }, [form, formRef]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-apple-dark">{t('forms.asset.basicInfo')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.assetName')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('forms.asset.placeholders.enterName')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.category')}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || undefined}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('forms.asset.placeholders.selectCategory')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {/* Equipment */}
                      <SelectItem value="Heavy Equipment">Heavy Equipment</SelectItem>
                      <SelectItem value="Light Equipment">Light Equipment</SelectItem>
                      <SelectItem value="Medical Equipment">Medical Equipment</SelectItem>
                      <SelectItem value="Office Equipment">Office Equipment</SelectItem>
                      <SelectItem value="IT Equipment">IT Equipment</SelectItem>
                      <SelectItem value="Laboratory Equipment">Laboratory Equipment</SelectItem>
                      <SelectItem value="Safety Equipment">Safety Equipment</SelectItem>
                      
                      {/* Vehicles */}
                      <SelectItem value="Commercial Vehicle">Commercial Vehicle</SelectItem>
                      <SelectItem value="Construction Vehicle">Construction Vehicle</SelectItem>
                      <SelectItem value="Emergency Vehicle">Emergency Vehicle</SelectItem>
                      <SelectItem value="Fleet Vehicle">Fleet Vehicle</SelectItem>
                      
                      {/* Tools */}
                      <SelectItem value="Hand Tools">Hand Tools</SelectItem>
                      <SelectItem value="Power Tools">Power Tools</SelectItem>
                      <SelectItem value="Precision Tools">Precision Tools</SelectItem>
                      <SelectItem value="Measuring Tools">Measuring Tools</SelectItem>
                      
                      {/* Machinery */}
                      <SelectItem value="Industrial Machinery">Industrial Machinery</SelectItem>
                      <SelectItem value="Manufacturing Machinery">Manufacturing Machinery</SelectItem>
                      <SelectItem value="Processing Machinery">Processing Machinery</SelectItem>
                      
                      {/* Infrastructure */}
                      <SelectItem value="Building">Building</SelectItem>
                      <SelectItem value="Facility">Facility</SelectItem>
                      <SelectItem value="Infrastructure">Infrastructure</SelectItem>
                      
                      {/* Technology */}
                      <SelectItem value="Software">Software</SelectItem>
                      <SelectItem value="Hardware">Hardware</SelectItem>
                      <SelectItem value="Network Equipment">Network Equipment</SelectItem>
                      
                      {/* Others */}
                      <SelectItem value="Furniture">Furniture</SelectItem>
                      <SelectItem value="Inventory">Inventory</SelectItem>
                      <SelectItem value="Supplies">Supplies</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="asset_description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('forms.asset.description')}</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t('forms.asset.placeholders.enterDescription')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Location Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-apple-dark">{t('forms.asset.location')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="company_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.company')}</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(Number(value));
                      form.setValue("place_id", null);
                    }}
                    value={field.value?.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('forms.asset.placeholders.selectCompany')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {companies?.map((company) => (
                        <SelectItem key={company.id} value={company.id.toString()}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="place_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.place')}</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      const numValue = Number(value);
                      field.onChange(numValue);
                      // Location will be updated by the effect above
                    }}
                    value={field.value?.toString()}
                    disabled={!form.watch("company_id")}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('forms.asset.placeholders.selectPlace')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {places?.map((place) => (
                        <SelectItem key={place.id} value={place.id.toString()}>
                          {place.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.specificLocation')}</FormLabel>
                  <div className="flex flex-col gap-2">
                    <FormControl>
                      <PlacesAutocomplete
                        value={field.value || ''}
                        onChange={(value) => {
                          field.onChange(value);
                        }}
                        onPlaceSelect={(place) => {
                          if (place.formatted_address) {
                            field.onChange(place.formatted_address);
                            form.setValue("location", place.formatted_address, {
                              shouldValidate: true,
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                          }

                          // Get coordinates from the place geometry
                          const lat = place.geometry?.location?.lat();
                          const lng = place.geometry?.location?.lng();
                          
                          if (typeof lat === 'number' && typeof lng === 'number') {
                            form.setValue("latitude", lat, {
                              shouldValidate: true,
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                            form.setValue("longitude", lng, {
                              shouldValidate: true,
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                          }
                        }}
                        placeholder={initialData?.location || t('forms.asset.placeholders.enterLocation')}
                      />
                    </FormControl>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex items-center gap-2"
                      onClick={() => {
                        if (navigator.geolocation) {
                          navigator.geolocation.getCurrentPosition(
                            async (position) => {
                              const { latitude, longitude } = position.coords;
                              
                              // Update coordinates
                              form.setValue("latitude", latitude, {
                                shouldValidate: true,
                                shouldDirty: true,
                                shouldTouch: true,
                              });
                              form.setValue("longitude", longitude, {
                                shouldValidate: true,
                                shouldDirty: true,
                                shouldTouch: true,
                              });

                              // Get address from coordinates using Google Geocoding API
                              try {
                                const geocoder = new google.maps.Geocoder();
                                const response = await geocoder.geocode({
                                  location: { lat: latitude, lng: longitude }
                                });

                                if (response.results[0]) {
                                  const address = response.results[0].formatted_address;
                                  field.onChange(address);
                                  form.setValue("location", address, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                    shouldTouch: true,
                                  });
                                }
                              } catch (error) {
                                console.error('Error getting address:', error);
                                toast({
                                  title: "Error",
                                  description: "Could not get address from coordinates",
                                  variant: "destructive",
                                });
                              }
                            },
                            (error) => {
                              console.error('Error getting location:', error);
                              toast({
                                title: "Error",
                                description: "Could not get your current location",
                                variant: "destructive",
                              });
                            }
                          );
                        } else {
                          toast({
                            title: "Error",
                            description: "Geolocation is not supported by your browser",
                            variant: "destructive",
                          });
                        }
                      }}
                    >
                      <MapPin className="h-4 w-4" />
                      <span>{t('forms.asset.buttons.getCurrentLocation')}</span>
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Hidden coordinate fields */}
            <FormField
              control={form.control}
              name="latitude"
              render={({ field }) => (
                <FormItem className="hidden">
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="longitude"
              render={({ field }) => (
                <FormItem className="hidden">
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-apple-dark">{t('forms.asset.contactInfo')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="asset_contact"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.contactPerson')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('forms.asset.placeholders.enterContact')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.phone')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('forms.asset.placeholders.enterPhone')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Asset Details */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-apple-dark">{t('forms.asset.assetDetails')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="internal_asset_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.internalId')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('forms.asset.placeholders.enterId')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="serial_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.serialNumber')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('forms.asset.placeholders.enterSerial')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nfc_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.nfcId')}</FormLabel>
                  <div className="flex space-x-2">
                    <FormControl>
                      <Input placeholder={t('forms.asset.placeholders.enterNfc')} {...field} />
                    </FormControl>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowNfcScanner(true)}
                      className="flex items-center space-x-2"
                    >
                      <Scan className="h-4 w-4" />
                      <span>{t('forms.asset.buttons.scan')}</span>
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="qr_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.qrId')}</FormLabel>
                  <div className="flex space-x-2">
                    <FormControl>
                      <Input placeholder={t('forms.asset.placeholders.enterQr')} {...field} />
                    </FormControl>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onShowCodeScanner}
                      className="flex items-center space-x-2"
                    >
                      <Scan className="h-4 w-4" />
                      <span>{t('forms.asset.buttons.scan')}</span>
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.code')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('forms.asset.placeholders.enterCode')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="asset_price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.price')}</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      placeholder={t('forms.asset.placeholders.enterPrice')} 
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(value === '' ? null : Number(value));
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.status')}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || undefined}>
                    <FormControl>
                      <SelectTrigger className={cn(field.value && getStatusColor(field.value))}>
                        <SelectValue placeholder={t('forms.asset.placeholders.selectStatus')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Available" className={getStatusColor('Available')}>Available</SelectItem>
                      <SelectItem value="In Use" className={getStatusColor('In Use')}>In Use</SelectItem>
                      <SelectItem value="Maintenance" className={getStatusColor('Maintenance')}>Maintenance</SelectItem>
                      <SelectItem value="Retired" className={getStatusColor('Retired')}>Retired</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="purchase_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.purchaseDate')}</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="warranty_expiry"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.warrantyExpiry')}</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rating"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.rating')}</FormLabel>
                  <Select 
                    onValueChange={(value) => field.onChange(value ? parseInt(value) : null)} 
                    value={field.value?.toString() ?? ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select rating (1-5)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="1">1 - Poor</SelectItem>
                      <SelectItem value="2">2 - Fair</SelectItem>
                      <SelectItem value="3">3 - Good</SelectItem>
                      <SelectItem value="4">4 - Very Good</SelectItem>
                      <SelectItem value="5">5 - Excellent</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="conditions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.conditions')}</FormLabel>
                  <Select 
                    onValueChange={(value) => field.onChange(value ? parseInt(value) : null)} 
                    value={field.value?.toString() ?? ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select condition (1-5)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="1">1 - Very Poor</SelectItem>
                      <SelectItem value="2">2 - Poor</SelectItem>
                      <SelectItem value="3">3 - Fair</SelectItem>
                      <SelectItem value="4">4 - Good</SelectItem>
                      <SelectItem value="5">5 - Excellent</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('forms.asset.notes')}</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t('forms.asset.placeholders.enterNotes')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Attachments */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-apple-dark">{t('forms.asset.attachments')}</h3>
          <div className="grid grid-cols-1 gap-4">
            <FormField
              control={form.control}
              name="images"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.images')}</FormLabel>
                  <FormControl>
                    <FileUploadField
                      label={t('forms.asset.images')}
                      accept="image/*"
                      onChange={setSelectedImages}
                      existingFiles={field.value ? field.value.split(',').filter(Boolean) : []}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="documents"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('forms.asset.documents')}</FormLabel>
                  <FormControl>
                    <FileUploadField
                      label={t('forms.asset.documents')}
                      accept=".pdf,.doc,.docx,.txt,.xls,.xlsx"
                      onChange={setSelectedDocuments}
                      existingFiles={field.value ? field.value.split(',').filter(Boolean) : []}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Privacy Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-apple-dark">{t('forms.asset.privacySettings')}</h3>
          <div className="grid grid-cols-1 gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="see_info"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>{t('forms.asset.seeInfo')}</FormLabel>
                      <p className="text-sm text-muted-foreground">
                        {t('forms.asset.seeInfoDesc')}
                      </p>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="see_records"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>{t('forms.asset.seeRecords')}</FormLabel>
                      <p className="text-sm text-muted-foreground">
                        {t('forms.asset.seeRecordsDesc')}
                      </p>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="add_records"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>{t('forms.asset.addRecords')}</FormLabel>
                      <p className="text-sm text-muted-foreground">
                        {t('forms.asset.addRecordsDesc')}
                      </p>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="private"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>{t('forms.asset.privateAsset')}</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      {t('forms.asset.privateAssetDesc')}
                    </p>
                  </div>
                </FormItem>
              )}
            />

            {form.watch("private") && (
              <FormField
                control={form.control}
                name="private_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('forms.asset.privateCode')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('forms.asset.placeholders.enterAccessCode')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/assets")}
            disabled={isSubmitting}
          >
            {t('forms.asset.buttons.cancel')}
          </Button>
          <Button 
            type="submit"
            disabled={isSubmitting}
            className="bg-blue-600 hover:bg-blue-700 text-white min-w-[100px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {mode === "create" ? t('forms.asset.buttons.creating') : t('forms.asset.buttons.updating')}
              </>
            ) : (
              mode === "create" ? t('forms.asset.buttons.create') : t('forms.asset.buttons.update')
            )}
          </Button>
        </div>

        {/* NFC Scanner Dialog */}
        <Dialog open={showNfcScanner} onOpenChange={setShowNfcScanner}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>{t('nfcScanner')}</DialogTitle>
              <DialogDescription>
                {t('holdNearTag')}
              </DialogDescription>
            </DialogHeader>

            {scanError && (
              <div className="w-full p-4 bg-red-50 rounded-xl">
                <p className="text-sm text-red-500 text-center">{scanError}</p>
              </div>
            )}

            <div className="space-y-4">
              <Button
                onClick={isScanning ? stopNfcScan : startNfcScan}
                variant={isScanning ? "secondary" : "default"}
                className="w-full h-14 text-lg rounded-xl relative"
                disabled={isScanning && !scanError}
              >
                {isScanning ? (
                  <div className="flex items-center space-x-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>{t('forms.asset.buttons.scanning')}</span>
                  </div>
                ) : (
                  t('forms.asset.buttons.startScan')
                )}
              </Button>

              {isScanning && (
                <Button
                  onClick={stopNfcScan}
                  variant="outline"
                  className="w-full h-14 text-lg rounded-xl"
                >
                  {t('forms.asset.buttons.cancelScan')}
                </Button>
              )}

              {isScanning && (
                <div className="text-center space-y-2">
                  <div className="w-16 h-16 mx-auto rounded-full bg-blue-50 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                  </div>
                  <p className="text-base text-gray-600">
                    {t('holdNearTag')}
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </form>
    </Form>
  );
}
